import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Field, TextIn } from "@/components/forms/Field";
import { publicUrl } from "@/lib/forms";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

function QrCard({ url, slug }) {
  const download = () => {
    const c = document.getElementById("form-qr");
    const a = document.createElement("a"); a.href = c.toDataURL("image/png"); a.download = `${slug}-qr.png`; a.click();
  };
  return (
    <div className="flex items-center gap-4" data-testid="form-qr-card">
      <div className="p-2 bg-white rounded"><QRCodeCanvas id="form-qr" value={url} size={112} level="M" /></div>
      <div className="space-y-2">
        <div className="text-xs text-neutral-500">Print it on event flyers, standees or slides.</div>
        <div className="flex gap-2">
          <button className="btn text-xs" onClick={download} data-testid="qr-download">Download PNG</button>
          <button className="btn-ghost btn text-xs" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} data-testid="qr-copy-link">Copy link</button>
        </div>
      </div>
    </div>
  );
}

function Variables({ s, set }) {
  const vars = s.variables || [];
  const upd = (i, p) => set({ variables: vars.map((v, j) => (j === i ? { ...v, ...p } : v)) });
  return (
    <section className="panel p-4 space-y-3" data-testid="variables-settings">
      <h3 className="text-sm font-medium">Calculated variables</h3>
      <div className="text-[11px] text-neutral-500">Formula with question ids and + - * / ( ), e.g. q_footfall * q_atp. Use in logic as var:id. Saved on the lead as var_id and, if set, into a lead field.</div>
      {vars.map((v, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_2fr_1fr_auto] gap-2 items-center" data-testid={`variable-${i}`}>
          <TextIn value={v.id} onChange={(x) => upd(i, { id: x.trim() })} placeholder="id (gmv)" testid={`variable-${i}-id`} />
          <TextIn value={v.label} onChange={(x) => upd(i, { label: x })} placeholder="Label" testid={`variable-${i}-label`} />
          <TextIn value={v.formula} onChange={(x) => upd(i, { formula: x })} placeholder="q_footfall * q_atp" testid={`variable-${i}-formula`} />
          <TextIn value={v.maps_to} onChange={(x) => upd(i, { maps_to: x.trim() })} placeholder="Lead field (gmv)" testid={`variable-${i}-maps-to`} />
          <button className="btn-ghost btn text-xs" onClick={() => set({ variables: vars.filter((_, j) => j !== i) })} data-testid={`variable-${i}-remove`}>Remove</button>
        </div>
      ))}
      <button className="btn text-xs" onClick={() => set({ variables: [...vars, { id: "", label: "", formula: "", maps_to: "" }] })} data-testid="variable-add">Add variable</button>
    </section>
  );
}

export default function FormSettingsPanel({ form, setForm }) {
  const s = form.settings || {};
  const set = (patch) => setForm({ ...form, settings: { ...s, ...patch } });

  const testHook = async () => {
    try {
      const { data } = await api.post(`/forms/${form._id}/test-webhook`);
      data.ok ? toast.success(`Zapier responded ${data.status}`) : toast.error(data.error || `Status ${data.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save the form first"); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="form-settings">
      <section className="panel p-4 space-y-3">
        <h3 className="text-sm font-medium">Link & tracking</h3>
        <Field label="Public link"><div className="text-sm font-mono text-neutral-300 break-all" data-testid="public-link">{publicUrl(form.slug)}</div></Field>
        <QrCard url={publicUrl(form.slug)} slug={form.slug} />
        <Field label="Extra hidden fields to capture from the URL (comma separated)"
          hint="utm_source, utm_medium, utm_campaign, utm_term, utm_content, fbclid, gclid, referrer and page URL are always captured.">
          <TextIn value={(s.hidden_fields || []).join(", ")} testid="hidden-fields"
            onChange={(v) => set({ hidden_fields: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
        </Field>
        <Field label="Meta Pixel ID" hint="Fires PageView on load and Lead on submit (eventID = lead id, dedupes with your CAPI hook). Leave empty for no pixel.">
          <TextIn value={s.meta_pixel_id} onChange={(v) => set({ meta_pixel_id: v.trim() })} placeholder="123456789012345" testid="meta-pixel-id" />
        </Field>
      </section>
      <Variables s={s} set={set} />
      <section className="panel p-4 space-y-3" data-testid="outbound-settings">
        <h3 className="text-sm font-medium">On submit, send to</h3>
        <div className="text-[11px] text-neutral-500">Skipped for leads that reach a disqualified ending.</div>
        <Field label="Zapier webhook URL" hint="Receives lead, answers, UTMs, outcome, score and meeting details as JSON.">
          <div className="flex gap-2">
            <TextIn value={s.zapier_webhook_url} onChange={(v) => set({ zapier_webhook_url: v })} placeholder="https://hooks.zapier.com/hooks/catch/…" testid="zapier-url" />
            <button className="btn shrink-0" onClick={testHook} data-testid="zapier-test-btn">Test</button>
          </div>
        </Field>
        <label className="flex items-center gap-2 text-sm" data-testid="zapier-new-only">
          <input type="checkbox" checked={s.zapier_new_leads_only !== false} onChange={(e) => set({ zapier_new_leads_only: e.target.checked })} data-testid="zapier-new-only-toggle" />
          Only send new leads (skip repeat submissions from existing leads)
        </label>
      </section>
    </div>
  );
}
